/**
 * 网页标题动态提示与闪烁工具
 * 用于在线客服等模块在收到新买家消息或存在未读消息时更新网页标题
 */

const DEFAULT_TITLE = '问卷助手';

function extractCleanTitle(raw?: string): string {
  if (!raw || typeof raw !== 'string') return DEFAULT_TITLE;
  const cleaned = raw.replace(/^[【\[][^】\]]*[】\]]\s*/, '').replace(/\s*-\s*有\d+\+?条新消息\s*$/, '').trim();
  return cleaned || DEFAULT_TITLE;
}

let originalTitle: string = typeof document !== 'undefined' ? extractCleanTitle(document.title) : DEFAULT_TITLE;
let currentUnreadCount = 0;
let flashTimer: any = null;
let flashStep = 0;
let isFlashing = false;
let listenersAttached = false;

/**
 * 格式化未读消息数
 * 超过 99 条显示 99+
 */
export function formatUnreadCount(count: number | string): string {
  const num = Number(count) || 0;
  if (num > 99) return '99+';
  return String(num);
}

/**
 * 获取基础网页标题
 */
export function getBaseTitle(count: number | string): string {
  const num = Number(count) || 0;
  const base = extractCleanTitle(originalTitle) || DEFAULT_TITLE;
  if (num <= 0) {
    return base;
  }
  return `${base} - 有${formatUnreadCount(num)}条新消息`;
}

/**
 * 渲染网页标题
 */
function renderTitle(): void {
  if (typeof document === 'undefined') return;

  const base = extractCleanTitle(originalTitle) || DEFAULT_TITLE;
  if (isFlashing && currentUnreadCount > 0) {
    const countStr = formatUnreadCount(currentUnreadCount);
    // 偶数步带【新消息】，奇数步带全角占位【　　　】，保证标签栏文字宽度一致不抖动
    if (flashStep % 2 === 0) {
      document.title = `【新消息】${base} - 有${countStr}条新消息`;
    } else {
      document.title = `【　　　】${base} - 有${countStr}条新消息`;
    }
  } else if (currentUnreadCount > 0) {
    document.title = getBaseTitle(currentUnreadCount);
  } else {
    document.title = base;
  }
}

/**
 * 开启交替闪烁
 */
function startFlashing(): void {
  if (flashTimer) return;
  isFlashing = true;
  flashStep = 0;
  renderTitle();
  flashTimer = setInterval(() => {
    flashStep++;
    renderTitle();
  }, 800);
}

/**
 * 停止交替闪烁
 */
function stopFlashing(): void {
  if (flashTimer) {
    clearInterval(flashTimer);
    flashTimer = null;
  }
  isFlashing = false;
  renderTitle();
}

/**
 * 窗口或标签页激活时回调
 */
function handleTabActive(): void {
  stopFlashing();
}

/**
 * 注册浏览器窗口激活与可见性监听
 */
function attachWindowListeners(): void {
  if (listenersAttached || typeof window === 'undefined') return;
  window.addEventListener('focus', handleTabActive);
  window.addEventListener('click', handleTabActive);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      handleTabActive();
    }
  });
  listenersAttached = true;
}

/**
 * 检查当前页面是否处于后台或失焦状态
 */
function isPageInBackground(): boolean {
  if (typeof document === 'undefined') return false;
  return Boolean(document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus()));
}

export interface SetUnreadOptions {
  triggerFlashIfBackground?: boolean;
}

/**
 * 设置当前未读消息数并更新标题
 * @param count 当前未读消息数
 * @param options 配置项
 */
export function setUnreadTitleCount(count: number | string, options: SetUnreadOptions = {}): void {
  const { triggerFlashIfBackground = true } = options;
  const num = Math.max(0, Number(count) || 0);
  const prevCount = currentUnreadCount;
  currentUnreadCount = num;

  attachWindowListeners();

  if (num <= 0) {
    resetPageTitle();
    return;
  }

  if (triggerFlashIfBackground && num > prevCount && isPageInBackground()) {
    startFlashing();
  } else if (!isFlashing) {
    renderTitle();
  }
}

/**
 * 收到新消息时主动调用
 * 若当前标签页在后台，则触发交替闪烁提醒；若在前台，则更新静态未读标题
 * @param count 当前最新的未读数
 */
export function notifyNewMessageTitle(count?: number): void {
  if (typeof count === 'number' && !isNaN(count)) {
    currentUnreadCount = Math.max(0, count);
  } else if (currentUnreadCount <= 0) {
    currentUnreadCount = 1;
  }

  attachWindowListeners();

  if (currentUnreadCount <= 0) {
    resetPageTitle();
    return;
  }

  if (isPageInBackground()) {
    startFlashing();
  } else {
    renderTitle();
  }
}

/**
 * 停止闪烁并恢复网页原始标题
 */
export function resetPageTitle(): void {
  if (flashTimer) {
    clearInterval(flashTimer);
    flashTimer = null;
  }
  isFlashing = false;
  currentUnreadCount = 0;
  if (typeof document !== 'undefined') {
    document.title = extractCleanTitle(originalTitle) || DEFAULT_TITLE;
  }
}

/**
 * 设置原始默认标题
 */
export function setOriginalTitle(title?: string): void {
  if (title && typeof title === 'string') {
    originalTitle = extractCleanTitle(title);
  }
}

export default {
  formatUnreadCount,
  getBaseTitle,
  setUnreadTitleCount,
  notifyNewMessageTitle,
  resetPageTitle,
  setOriginalTitle
};
